import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FolderOpen,
  Plus,
  Search,
  FileText,
  Upload,
  Download,
  PenTool,
  CheckCircle,
  Clock,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const documentTypes = [
  { value: 'hr', label: 'HR Document' },
  { value: 'client', label: 'Client Document' },
  { value: 'training', label: 'Training Material' },
  { value: 'policy', label: 'Policy & Procedure' },
  { value: 'assessment', label: 'Assessment' },
];

export default function Documents() {
  const { user, organization } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSignDialogOpen, setIsSignDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [signatureData, setSignatureData] = useState('');
  const [newDocument, setNewDocument] = useState({
    title: '',
    document_type: 'policy',
    content: '',
    requires_signature: false,
    related_client_id: '',
    related_staff_id: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization, filterType]);

  const fetchData = async () => {
    try {
      const params = { organization_id: organization?.id };
      if (filterType !== 'all') params.document_type = filterType;
      
      const response = await axios.get(`${API_URL}/api/documents`, { params });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/documents`, {
        ...newDocument,
        organization_id: organization?.id,
        uploaded_by: user?.id,
      });
      toast.success('Document created successfully');
      setIsDialogOpen(false);
      setNewDocument({
        title: '',
        document_type: 'policy',
        content: '',
        requires_signature: false,
        related_client_id: '',
        related_staff_id: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Failed to create document');
    }
  };

  const handleSignDocument = async () => {
    if (!signatureData.trim()) {
      toast.error('Please enter your signature');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/documents/sign`, {
        document_id: selectedDoc.id,
        signer_id: user?.id,
        signer_name: `${user?.first_name} ${user?.last_name}`,
        signature_data: signatureData,
      });
      toast.success('Document signed successfully');
      setIsSignDialogOpen(false);
      setSelectedDoc(null);
      setSignatureData('');
      fetchData();
    } catch (error) {
      console.error('Error signing document:', error);
      toast.error('Failed to sign document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    return doc.title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getTypeLabel = (type) => {
    const docType = documentTypes.find(t => t.value === type);
    return docType?.label || type;
  };

  return (
    <div className="min-h-screen" data-testid="documents-page">
      <Header title="Documents" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="doc-search"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-44" data-testid="type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {documentTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="upload-doc-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Document</DialogTitle>
                <DialogDescription>
                  Create a new document or policy for your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateDocument} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Document Title *</Label>
                  <Input
                    id="title"
                    value={newDocument.title}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Health & Safety Policy"
                    required
                    data-testid="doc-title"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="document_type">Document Type *</Label>
                    <Select 
                      value={newDocument.document_type}
                      onValueChange={(v) => setNewDocument(prev => ({ ...prev, document_type: v }))}
                    >
                      <SelectTrigger data-testid="doc-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newDocument.requires_signature}
                        onChange={(e) => setNewDocument(prev => ({ ...prev, requires_signature: e.target.checked }))}
                        className="rounded border-slate-300"
                        data-testid="doc-requires-signature"
                      />
                      <span className="text-sm">Requires Signature</span>
                    </label>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content">Document Content *</Label>
                  <Textarea
                    id="content"
                    value={newDocument.content}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, content: e.target.value }))}
                    rows={8}
                    placeholder="Enter document content..."
                    required
                    data-testid="doc-content"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-doc">
                    Create Document
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Documents</p>
                  <p className="text-3xl font-bold">{documents.length}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Signature</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {documents.filter(d => d.requires_signature && (!d.signed_by || d.signed_by.length === 0)).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Policies</p>
                  <p className="text-3xl font-bold">
                    {documents.filter(d => d.document_type === 'policy').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Signed</p>
                  <p className="text-3xl font-bold text-green-600">
                    {documents.filter(d => d.signed_by && d.signed_by.length > 0).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        <Card data-testid="documents-list">
          <CardHeader>
            <CardTitle>All Documents</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-40 bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredDocuments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDocuments.map((doc) => (
                  <div 
                    key={doc.id}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                    data-testid={`doc-card-${doc.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-teal-600" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {getTypeLabel(doc.document_type)}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{doc.title}</h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">{doc.content}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {new Date(doc.created_at).toLocaleDateString('en-GB')}
                      </span>
                      <div className="flex gap-2">
                        {doc.requires_signature && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setIsSignDialogOpen(true);
                            }}
                            className="text-xs"
                            disabled={doc.signed_by?.includes(user?.id)}
                            data-testid={`sign-btn-${doc.id}`}
                          >
                            <PenTool className="w-3 h-3 mr-1" />
                            {doc.signed_by?.includes(user?.id) ? 'Signed' : 'Sign'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No documents found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Create First Document
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sign Dialog */}
        <Dialog open={isSignDialogOpen} onOpenChange={setIsSignDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Sign Document</DialogTitle>
              <DialogDescription>
                Sign "{selectedDoc?.title}" electronically
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                <Label htmlFor="signature">Type your full name as signature</Label>
                <Input
                  id="signature"
                  value={signatureData}
                  onChange={(e) => setSignatureData(e.target.value)}
                  placeholder={`${user?.first_name} ${user?.last_name}`}
                  className="mt-2 text-lg font-signature"
                  data-testid="signature-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                By signing, you confirm that you have read and understood this document.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsSignDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSignDocument}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="confirm-sign"
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Sign Document
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
